/**
 * The builder's dog, who helps with everything and is no help at all.
 */

import type { Ink } from "./ink.js";
import { mix } from "./timing.js";

export type DogMode = "walk" | "run" | "sit" | "sleep" | "jump";

export interface Dog {
  x: number;
  ground: number;
  facing: 1 | -1;
  mode: DogMode;
  /** The walk's phase, in steps; also the tail's wag. */
  phase?: number;
  /** For a jump, how far through it: 0–1. */
  air?: number;
  /** Something held in the mouth: a stick of lumber, mostly. */
  carry?: boolean;
  scale?: number;
  /** Time, for the wag and the breathing of a sleeping dog. */
  t: number;
}

export function drawDog(ink: Ink, dog: Dog): void {
  const p = ink.palette;
  const { ctx } = ink;
  const s = dog.scale ?? 1;
  const phase = dog.phase ?? 0;
  ctx.save();
  ctx.translate(dog.x, dog.ground);
  ctx.scale(dog.facing * s, s);

  if (dog.mode === "sleep") {
    const breath = Math.sin(dog.t * 2.2) * 1.2;
    ink.ellipse(0, -13, 30, 13 + breath * 0.4, { fill: p.paper, width: 3, offset: 0 });
    ink.circle(24, -12, 10, { fill: p.paper, width: 3, offset: 0 });
    ink.shape(
      [
        { x: 19, y: -20 },
        { x: 27, y: -21 },
        { x: 25, y: -6 },
      ],
      { fill: p.woodDark, width: 2.4, offset: 0 },
    );
    ink.line(26, -12, 31, -12, { width: 1.8, overshoot: false });
    ink.curve(
      [
        { x: -28, y: -10 },
        { x: -36, y: -4 },
        { x: -28, y: 0 },
      ],
      { width: 3 },
    );
    const z = (dog.t * 0.9) % 1;
    for (let i = 0; i < 2; i += 1) {
      const k = (z + i * 0.5) % 1;
      ink.text("z", 36 + k * 14, -26 - k * 34, { size: 14 + k * 8, alpha: 1 - k, weight: 800 });
    }
    ctx.restore();
    return;
  }

  const lift = dog.mode === "jump" ? -Math.sin((dog.air ?? 0) * Math.PI) * 46 : 0;
  const sitting = dog.mode === "sit";
  const bob = dog.mode === "walk" || dog.mode === "run" ? Math.abs(Math.sin(phase * Math.PI)) * -3 : 0;
  ctx.translate(0, lift + bob);
  const bodyY = -30;
  const tilt = sitting ? -0.45 : dog.mode === "jump" ? -0.2 : 0;

  // Legs: the far pair lighter, behind the body.
  const stride = dog.mode === "run" ? 0.85 : dog.mode === "walk" ? 0.5 : 0;
  const swing = (offset: number): number => Math.sin((phase + offset) * Math.PI) * stride;
  const leg = (hipX: number, angle: number, alpha: number, length = 24): void => {
    const hipY = bodyY + 6 + (sitting && hipX < 0 ? 12 : 0);
    ink.line(hipX, hipY, hipX + Math.sin(angle) * length, hipY + Math.cos(angle) * length, {
      width: 3.4,
      alpha,
      overshoot: false,
    });
  };
  if (sitting) {
    leg(17, 0.05, 0.55);
    leg(-14, 1.2, 0.55, 16);
  } else {
    leg(16, swing(0.5), 0.55);
    leg(-17, swing(0), 0.55);
  }

  const wag = Math.sin(dog.t * (sitting ? 9 : 14)) * 0.5;
  const tailRoot = { x: -27, y: bodyY - 4 + (sitting ? 10 : 0) };
  ink.curve(
    [
      tailRoot,
      { x: tailRoot.x - 10, y: tailRoot.y - 10 + wag * 6 },
      { x: tailRoot.x - 14 + wag * 8, y: tailRoot.y - 24 },
    ],
    { width: 3.4 },
  );

  ctx.save();
  ctx.translate(0, bodyY);
  ctx.rotate(tilt);
  ink.ellipse(0, 0, 29, 13, { fill: p.paper, width: 3, offset: 0 });
  ink.ellipse(-6, -4, 9, 6, { fill: p.woodDark, stroke: false, offset: 0 });
  ctx.restore();

  if (sitting) {
    leg(20, -0.05, 1);
    leg(-12, 1.4, 1, 14);
  } else {
    leg(19, swing(1.5), 1);
    leg(-14, swing(1), 1);
  }

  // Head, collar, ear.
  const headX = sitting ? 24 : 30;
  const headY = sitting ? -62 : -44;
  ink.line(headX - 9, headY + 9, headX - 4, headY + 14, { width: 4, color: p.signal, overshoot: false });
  ink.circle(headX, headY, 11, { fill: p.paper, width: 3, offset: 0 });
  ink.ellipse(headX + 12, headY + 4, 7, 5, { fill: p.paper, width: 2.6, offset: 0 });
  ink.dot(headX + 18, headY + 2, 2.6);
  ink.dot(headX + 4, headY - 3, 2);
  const flop = Math.sin(dog.t * 7) * (dog.mode === "run" ? 0.5 : 0.12);
  ink.shape(
    [
      { x: headX - 7, y: headY - 9 },
      { x: headX + 1, y: headY - 10 },
      { x: mix(headX - 6, headX - 14, flop + 0.3), y: headY + 7 },
    ],
    { fill: p.woodDark, width: 2.4, offset: 0 },
  );
  if (dog.carry === true) {
    ink.rect(headX - 6, headY + 7, 44, 6, { fill: p.wood, width: 2.2, offset: 1 });
  } else if (dog.mode === "run" || dog.mode === "jump") {
    ink.ellipse(headX + 14, headY + 11, 3, 5, { fill: p.signal, width: 1.6, offset: 0 });
  }
  ctx.restore();
}
